import type { ModuleId } from "@playbookai/ui";
import type { SecureCollabProtocolMode } from "@playbookai/collab-client";
import {
  INSTALLABLE_MODULE_KEYS,
  PRODUCT_NAME,
  isInstallableModuleKey,
  type InstallableModuleKey,
} from "@playbookai/types";

/**
 * Typed reader for the runtime config that's loaded from `/config.js` before
 * the SPA boots. The file ships in `public/config.js` for dev; in Kubernetes
 * it's mounted from a ConfigMap so each customer's deployment can toggle
 * modules without a rebuild.
 *
 * See ADR-0009 (frontend package & module topology) for the rationale and
 * post-MVP plans (build-time tree-shaking, server-driven enablement).
 */
export interface RuntimeConfig {
  readonly vsmMapArrowAuto: boolean;
  /** Parent Hoshin picker only; does not disable existing child plans. */
  readonly hoshinParentPlanSelectionEnabled: boolean;
  readonly modules: Readonly<Record<ModuleId, boolean>>;
  /**
   * The modules this deployment installed (ADR-0100), in the installable
   * vocabulary — so it can say `kpi`, which `modules` cannot. Only known keys.
   */
  readonly installedModules: readonly InstallableModuleKey[];
  readonly api: {
    readonly notificationBaseUrl: string;
    /**
     * WebSocket URL for the live bell, e.g. `wss://host/ws/notifications`.
     *
     * EMPTY disables the socket, which is the default and is safe: the bell
     * falls back to its ordinary REST reads. It is empty rather than derived
     * from the page origin because the socket has its own ingress path and its
     * own host in some environments.
     */
    readonly notificationSocketUrl: string;
  };
  /**
   * The installed release, for the Admin console's health surface.
   *
   * Deploy-time config rather than a build constant: the same image is
   * promoted across environments, so a value baked at build time would report
   * whatever was built rather than what is running. Empty means the deployment
   * did not state one, which the console reports as unknown rather than
   * inventing a version.
   */
  readonly deploymentVersion: string;
  readonly playerAi: {
    readonly groupCollaborationEnabled: boolean;
    /**
     * Read from `PLAYERAI_SHOW_DEVELOPER_BOTTOM_BAR` — the setting's requested
     * name, and the one an operator sets, in `/config.js` locally and in the
     * Shell chart's runtime ConfigMap in a cluster. (The shell has no
     * environment at runtime, so that document IS its environment.) This field
     * is the internal, camel-cased reading of it.
     *
     * Presentation only: the developer footer under the Player AI composer
     * (raw Run status, the AI profile, diagnostic badges). Default `false`, and
     * when it is false the footer row is not rendered at all.
     *
     * It changes nothing about Run execution or composer eligibility, and it
     * never hides an actionable state — approval requests, upload waits,
     * connection failures and the reason a disabled input is disabled are all
     * rendered elsewhere and are unaffected.
     */
    readonly showDeveloperBottomBar: boolean;
    /**
     * Read from `playerAi.pollFallback` (ADR-0009, the one bounded exception to
     * "no feature flags" for this setting). Whether the Player AI client may
     * fall back to long polling when the live line (SSE) is proven broken, for
     * example behind a company network filter that holds the stream. The live
     * line is always tried first; only `true` lets the client poll after it
     * fails, so a deployment that says nothing never polls.
     *
     * Default `false`, and only a real `true` (or the strings `"true"` / `"1"`)
     * turns it on, exactly as for the other deploy-time booleans. A new value
     * takes effect on the next full page load.
     */
    readonly pollFallback: boolean;
  };
  readonly links: {
    /**
     * Settings → Account → Quick links → Documentation. Deploy-time config
     * because the destination is the customer's own documentation, not a
     * build constant. Empty hides the link rather than inventing one.
     */
    readonly documentationUrl: string;
  };
}

// `installedModules` has no fixed default: the reader derives it from `modules`.
const DEFAULT_CONFIG: Omit<RuntimeConfig, "installedModules"> = {
  vsmMapArrowAuto: true,
  hoshinParentPlanSelectionEnabled: false,
  modules: {
    solve: true,
    vsm: false,
    hoshin: false,
    dpm: false,
    lama: false,
    knowledge_studio: false,
  },
  api: {
    notificationBaseUrl: "/api",
    notificationSocketUrl: "",
  },
  deploymentVersion: "",
  playerAi: {
    groupCollaborationEnabled: true,
    showDeveloperBottomBar: false,
    pollFallback: false,
  },
  links: {
    documentationUrl: "",
  },
};

/**
 * Read an explicit deploy-time boolean, defaulting to `false`.
 *
 * A ConfigMap rendered from an environment variable delivers `"true"` /
 * `"false"` as STRINGS, and `Boolean("false")` is `true` — which is exactly how
 * a setting meant to default to hidden ends up on everywhere. So only a real
 * `true`, or the literal strings `"true"` / `"1"` (case- and space-insensitive),
 * turn it on; `"false"`, `"0"`, `""`, a number, an object, a misspelling and an
 * absent key all read as `false`.
 */
function explicitTrue(value: boolean | string | undefined): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return false;
  const normalized = value.trim().toLowerCase();
  return normalized === "true" || normalized === "1";
}

/** Only an absolute http(s) URL is a destination; anything else hides the link. */
function httpUrlOrEmpty(value: string | undefined): string {
  const trimmed = value?.trim() ?? "";
  try {
    const url = new URL(trimmed);
    return url.protocol === "http:" || url.protocol === "https:" ? trimmed : "";
  } catch {
    return "";
  }
}

/**
 * Read `installedModules`, keeping only keys this platform knows: it only ever
 * narrows what the session claim allows, and the server is the authority.
 *
 * Absent (an environment whose config predates the list, local dev without
 * it, a test stub, a rollback): the `true` keys of `modules` plus `kpi`. KPI
 * Manager has always been on with no shell switch, so this changes nothing.
 */
function readInstalledModules(
  value: unknown,
  modules: Readonly<Record<ModuleId, boolean>>,
): readonly InstallableModuleKey[] {
  if (!Array.isArray(value)) {
    return INSTALLABLE_MODULE_KEYS.filter(
      (key) => key === "kpi" || modules[key],
    );
  }
  return value.filter(
    (key): key is InstallableModuleKey =>
      typeof key === "string" && isInstallableModuleKey(key),
  );
}

/**
 * Read the runtime config, falling back to safe defaults when the script is
 * missing or partially populated. Always returns a fully-typed `RuntimeConfig`.
 */
export function getRuntimeConfig(): RuntimeConfig {
  const fromWindow =
    typeof window !== "undefined" ? window.__PB_CONFIG__ : undefined;
  const modulesIn = fromWindow?.modules ?? {};
  const apiIn = fromWindow?.api ?? {};
  const playerAiIn = fromWindow?.playerAi ?? {};
  const modules: RuntimeConfig["modules"] = {
    solve: modulesIn.solve ?? DEFAULT_CONFIG.modules.solve,
    vsm: modulesIn.vsm ?? DEFAULT_CONFIG.modules.vsm,
    hoshin: modulesIn.hoshin ?? DEFAULT_CONFIG.modules.hoshin,
    dpm: modulesIn.dpm ?? DEFAULT_CONFIG.modules.dpm,
    lama: modulesIn.lama ?? DEFAULT_CONFIG.modules.lama,
    knowledge_studio:
      modulesIn.knowledge_studio ?? DEFAULT_CONFIG.modules.knowledge_studio,
  };
  return {
    hoshinParentPlanSelectionEnabled: explicitTrue(
      fromWindow?.HOSHIN_PARENT_PLAN_SELECTION_ENABLED,
    ),
    vsmMapArrowAuto:
      fromWindow?.VSM_MAP_ARROW_AUTO == null
        ? true
        : explicitTrue(fromWindow.VSM_MAP_ARROW_AUTO),
    modules,
    installedModules: readInstalledModules(
      fromWindow?.installedModules,
      modules,
    ),
    api: {
      notificationBaseUrl:
        apiIn.notificationBaseUrl ?? DEFAULT_CONFIG.api.notificationBaseUrl,
      notificationSocketUrl:
        apiIn.notificationSocketUrl ?? DEFAULT_CONFIG.api.notificationSocketUrl,
    },
    deploymentVersion:
      fromWindow?.deploymentVersion?.trim() || DEFAULT_CONFIG.deploymentVersion,
    playerAi: {
      groupCollaborationEnabled:
        playerAiIn.groupCollaborationEnabled ??
        DEFAULT_CONFIG.playerAi.groupCollaborationEnabled,
      showDeveloperBottomBar: explicitTrue(
        playerAiIn.PLAYERAI_SHOW_DEVELOPER_BOTTOM_BAR,
      ),
      pollFallback: explicitTrue(playerAiIn.pollFallback),
    },
    links: {
      documentationUrl: httpUrlOrEmpty(fromWindow?.links?.documentationUrl),
    },
  };
}

export function getDocumentationUrl(): string {
  return getRuntimeConfig().links.documentationUrl;
}

/**
 * The chrome's top-left lockup: the tenant's organisation name, with its own
 * initial as the monogram (PB-5176 d6, as the Direction A artboard draws it;
 * supersedes the 2026-08-11 "product - organisation" lockup of PB-35/36/39).
 * The organisation changes without a release from Admin → Organization. Falls
 * back to the product name (`PRODUCT_NAME`) when a session carries no tenant name,
 * which is what a pre-tenantName session looks like until its next token
 * refresh — a lockup with nothing to brand would be worse than the product.
 */
export function getBrandLabel(organisationName: string | null): string {
  const organisation = organisationName?.trim();
  return organisation || PRODUCT_NAME;
}

/**
 * Is the Player AI SOLVE group collaboration surface enabled for this
 * deployment (ADR-0044)? Affordance-only: the Player AI service re-decides
 * every group action, so a stale `true` here can never authorize anything.
 */
export function isGroupCollaborationEnabled(): boolean {
  return getRuntimeConfig().playerAi.groupCollaborationEnabled;
}

/**
 * `PLAYERAI_SHOW_DEVELOPER_BOTTOM_BAR`: show the Player AI developer footer?
 *
 * Read at render time from `window.__PB_CONFIG__`, so a new value takes effect
 * on the next full page load of the SPA — a ConfigMap change plus a shell pod
 * roll, with no frontend rebuild. Hot reloading is deliberately not attempted:
 * `/config.js` is a plain script evaluated once before the entry point.
 */
export function isDeveloperBottomBarVisible(): boolean {
  return getRuntimeConfig().playerAi.showDeveloperBottomBar;
}

/**
 * Convenience: is a given module enabled in the current runtime config? An
 * installable module must also be in `installedModules` (ADR-0100), so its
 * old flag can only narrow that one list, never widen it.
 */
export function isModuleEnabled(id: ModuleId): boolean {
  const cfg = getRuntimeConfig();
  return (
    cfg.modules[id] &&
    (!isInstallableModuleKey(id) || cfg.installedModules.includes(id))
  );
}

/**
 * Collaboration admission is a cutover control, so it deliberately has no
 * browser fallback. A missing or malformed deploy-time value must stop the
 * provider from mounting instead of silently selecting either protocol.
 */
export function getCollaborationProtocolMode(): SecureCollabProtocolMode {
  const mode =
    typeof window !== "undefined"
      ? window.__PB_CONFIG__?.collaboration?.protocolMode
      : undefined;
  if (mode !== "legacy" && mode !== "secure") {
    throw new Error(
      "window.__PB_CONFIG__.collaboration.protocolMode must be legacy or secure",
    );
  }
  return mode;
}

/** The live-canvas address, and why a configured value was refused (if it was). */
export interface CollaborationWsUrlResolution {
  readonly url: string;
  readonly problem?: string;
}

/** `/` then a non-`/` character; no query or fragment. */
const COLLABORATION_PATH = /^\/[^/?#][^?#]*$/;
/**
 * `ws://` or `wss://`, a host (letters, digits, `.`, `-` and `_`, so no
 * `user@` and no `%`; or a bracketed IPv6 address of up to eight hex groups),
 * an optional port 0–65535, an optional path; no query or fragment. The Shell
 * chart applies the same pattern at render time.
 */
const COLLABORATION_ABSOLUTE =
  /^wss?:\/\/(?:\[(?:[0-9a-f]{0,4}:){2,7}[0-9a-f]{0,4}\]|[a-z0-9._-]+)(?::(?:6553[0-5]|655[0-2]\d|65[0-4]\d{2}|6[0-4]\d{3}|[1-5]\d{4}|\d{1,4}))?(?:\/[^?#]*)?$/i;

/** The URL parser must accept it too, or `new WebSocket` would throw. */
function parsesAsSocketUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      (url.protocol === "ws:" || url.protocol === "wss:") && url.hostname !== ""
    );
  } catch {
    return false;
  }
}

/**
 * Pick the live-canvas (collab) WebSocket address at runtime, so one Shell
 * image works at every site (PB-5510). The address used to be written into the
 * bundle at build time, which pointed every copy of the image at the site it
 * was built for.
 *
 * `configured` is `collaboration.url` from `/config.js`. Missing, empty or
 * blank means automatic: this page's own host + `/collab`, which is where every
 * environment routes collab. A `/path` is resolved on this page's host; a
 * `ws://` or `wss://` address is used as-is. The Helm chart refuses anything
 * else at render time; the browser adds the one rule the chart cannot know: a
 * `ws://` address on an https page, which the browser would block.
 *
 * A refused value is never fatal: it comes back as `problem` with the automatic
 * address, because a wrong setting must not take the whole app down.
 */
export function resolveCollaborationWsUrl(
  configured: unknown,
  location: Pick<Location, "protocol" | "host">,
): CollaborationWsUrlResolution {
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  const automatic = `${scheme}//${location.host}/collab`;
  if (configured === undefined || configured === null) {
    return { url: automatic };
  }
  if (typeof configured !== "string") {
    return {
      url: automatic,
      problem: "collaboration.url must be a string",
    };
  }
  const value = configured.trim();
  if (value === "") return { url: automatic };
  if (COLLABORATION_PATH.test(value)) {
    return { url: `${scheme}//${location.host}${value}` };
  }
  if (COLLABORATION_ABSOLUTE.test(value) && parsesAsSocketUrl(value)) {
    if (scheme === "wss:" && value.toLowerCase().startsWith("ws:")) {
      return {
        url: automatic,
        problem: `collaboration.url "${value}" is ws:// on an https page, which the browser blocks`,
      };
    }
    return { url: value };
  }
  return {
    url: automatic,
    problem: `collaboration.url "${value}" must be a /path or a ws:// or wss:// address without ? or #`,
  };
}

/** The live-canvas address for this page; warns when a setting was refused. */
export function getCollaborationWsUrl(): string {
  const { url, problem } = resolveCollaborationWsUrl(
    window.__PB_CONFIG__?.collaboration?.url,
    window.location,
  );
  if (problem) console.warn(`[collaboration] ${problem}; using ${url}`);
  return url;
}
